<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'CreateFollowMarketingCampaign' }" class="btn btn-primary">
                <i class="ri-add-line"></i>
                新增加好友行銷
              </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱或代碼..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchCampaigns">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>活動代碼</th>
            <th>活動名稱</th>
            <th>LINE OA</th>
            <th>狀態</th>
            <th>活動期間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="campaigns.length === 0">
            <td colspan="6" class="empty-state">尚無加好友行銷活動</td>
          </tr>
          <tr v-for="campaign in campaigns" :key="campaign.id">
            <td>{{ campaign.campaignCode }}</td>
            <td>{{ campaign.campaignName }}</td>
            <td>{{ campaign.lineConfigName || '-' }}</td>
            <td>
              <span :class="campaign.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ campaign.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatPeriod(campaign.startDate, campaign.endDate) }}</td>
            <td>
              <div class="action-buttons">
                <router-link
                  :to="{ name: 'EditFollowMarketingCampaign', params: { id: campaign.id } }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </router-link>
                <button class="btn btn-sm btn-danger" @click="handleDelete(campaign)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { followMarketingCampaignService } from '@/api/modules/followMarketingCampaigns.js';
import { showDeleteConfirm, showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'FollowMarketingCampaignList',
  setup() {
    const { pageTitle } = usePageTitle('加好友行銷');
    const campaigns = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pagination = reactive({
      totalPages: 1,
      totalItems: 0
    });

    const fetchCampaigns = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize: 20
        };
        if (searchQuery.value.trim()) params.search = searchQuery.value.trim();
        if (filterStatus.value === 'active') params.isActive = true;
        if (filterStatus.value === 'inactive') params.isActive = false;

        const res = await followMarketingCampaignService.getCampaigns(params);
        const data = res.data?.data ?? res.data;
        campaigns.value = data?.items || [];
        Object.assign(pagination, data?.pagination || {});
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const goToPage = (p) => {
      currentPage.value = p;
      fetchCampaigns();
    };

    const formatPeriod = (start, end) => {
      const hasStart = start != null && start !== '';
      const hasEnd = end != null && end !== '';
      if (!hasStart && !hasEnd) return '隨時開始～永久有效';
      const fmt = (v) => new Date(v).toLocaleString('zh-TW');
      if (hasStart && hasEnd) return `${fmt(start)} ~ ${fmt(end)}`;
      if (hasStart) return `${fmt(start)} ～ 永久有效`;
      return `隨時開始 ～ ${fmt(end)}`;
    };

    const handleDelete = async (campaign) => {
      const ok = await showDeleteConfirm(`確定刪除「${campaign.campaignName}」？`);
      if (!ok) return;
      try {
        await followMarketingCampaignService.deleteCampaign(campaign.id);
        showSuccess('已刪除');
        await fetchCampaigns();
      } catch (e) {
        showError(e.response?.data?.message || e.message || '刪除失敗');
      }
    };

    onMounted(fetchCampaigns);

    return {
      pageTitle,
      campaigns,
      loading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      pagination,
      fetchCampaigns,
      handleSearch,
      handleFilterChange,
      goToPage,
      formatPeriod,
      handleDelete
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 960px;
}
</style>
